import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { DatePicker, Space } from 'antd';
import axios from 'axios';
import 'antd/dist/reset.css';

const { RangePicker } = DatePicker;

// Helper function to format values consistently
// Replace the formatValue helper with this expanded version
const formatValue = (key, value, lead) => {
  if (key === 'supplier_total') {
    const total = lead?.suppliers?.reduce((s, x) => s + (Number(x.total_payable) || 0), 0) || 0;
    return `₹${total.toFixed(2)}`;
  }
  if (key === 'supplier_paid') {
    const paid = (lead?.payment_logs || []).reduce((s, x) => s + (Number(x.paid_amount) || 0), 0);
    return `₹${paid.toFixed(2)}`;
  }
  if (key === 'supplier_balance') {
    const total = lead?.suppliers?.reduce((s, x) => s + (Number(x.total_payable) || 0), 0) || 0;
    const paid = (lead?.payment_logs || []).reduce((s, x) => s + (Number(x.paid_amount) || 0), 0);
    return `₹${(total - paid).toFixed(2)}`;
  }
  if (key === 'receivable_total') {
    return `₹${(Number(lead?.approx_budget) || 0).toFixed(2)}`;
  }
  if (key === 'receivable_paid') {
    const paid = (lead?.receivables || []).reduce((s, r) => s + (Number(r.paid_amount) || 0), 0);
    return `₹${paid.toFixed(2)}`;
  }
  if (key === 'receivable_balance') {
    const total = Number(lead?.approx_budget) || 0;
    const paid = (lead?.receivables || []).reduce((s, r) => s + (Number(r.paid_amount) || 0), 0);
    return `₹${(total - paid).toFixed(2)}`;
  }

  // existing logic below
  if (!value) return '-';
  if (key === 'created_at' || key === 'updated_at' || key === 'travel_created_at' || key === 'most_recent_updated_at' || key === 'start_date' || key === 'end_date') {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
  if (key === 'tag_names' && Array.isArray(value)) return value.join(', ');
  return value;
};
const ReportComments = ({ 
  show, 
  onHide, 
  selectedLead, 
  baseURL,
  allFields = [] 
}) => {
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);

  // Filter out 'comment' field from display fields
  const displayFields = allFields.map(f => f.key).filter(k => k !== 'comment');

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setComments([]);
      setDateRange(null);
    }
  }, [show]);

  // Fetch comments when modal opens
  useEffect(() => {
    if (selectedLead && show) {
      fetchComments(selectedLead.leadid);
    }
  }, [selectedLead, show]);

  const fetchComments = async (leadid) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${baseURL}/comments-report/${leadid}`);
      const sortedComments = response.data.sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
      );
      setComments(sortedComments);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredComments = comments.filter(comment => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return true;
    const commentDate = new Date(comment.timestamp);
    const start = dateRange[0].toDate();
    const end = dateRange[1].toDate();
    end.setSeconds(59);
    end.setMilliseconds(999);
    return commentDate >= start && commentDate <= end;
  });

  const styles = {
    modalBody: {
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      height: '70vh',
    },
    topBar: {
      padding: '10px 16px',
      borderBottom: '1px solid #e8e8e8',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      backgroundColor: '#fafafa',
      flexShrink: 0,
    },
    contentWrapper: {
      display: 'flex',
      flex: 1,
      overflow: 'hidden',
    },
    leadDetailsColumn: {
      width: '42%',
      borderRight: '1px solid #e0c090',
      overflowY: 'auto',
      padding: '16px',
      backgroundColor: '#fffaf6',
    },
    commentsColumn: {
      width: '58%',
      overflowY: 'auto',
      padding: '16px',
      backgroundColor: '#fff',
    },
    leadBadge: {
      display: 'inline-block',
      background: '#ff9966',
      color: '#fff',
      borderRadius: 4,
      padding: '4px 12px',
      fontSize: 12,
      fontWeight: 700,
      marginBottom: 16,
    },
    fieldsGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '8px 12px',
    },
    fieldCell: {
      padding: '8px 10px',
      backgroundColor: '#fff',
      border: '1px solid #f0e8e0',
      borderRadius: 6,
    },
    fieldLabel: {
      fontSize: 10,
      color: '#a36e29',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.3,
      marginBottom: 4,
    },
    fieldValue: {
      fontSize: 12,
      color: '#222',
      fontWeight: 500,
      wordBreak: 'break-word',
    },
    commentCard: {
      marginBottom: 12,
      padding: '10px 12px',
      backgroundColor: '#fff7f2',
      border: '1px solid #f0e0d0',
      borderRadius: 6,
    },
    commentTime: {
      fontSize: 11,
      color: '#999',
      marginBottom: 4,
    },
    commentText: {
      fontSize: 13,
      color: '#333',
      whiteSpace: 'pre-line',
    },
    commentAuthor: {
      color: '#a36e29',
      fontWeight: 600,
    },
    emptyState: {
      textAlign: 'center',
      color: '#aaa',
      marginTop: 40,
      fontSize: 14,
    },
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header
        closeButton
        style={{ backgroundColor: 'rgba(163,110,41,0.08)', borderBottom: '1px solid #f0d0b0' }}
      >
        <Modal.Title style={{ fontSize: 16, fontWeight: 600, color: '#a36e29' }}>
          Comments for {selectedLead?.name || 'Lead'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={styles.modalBody}>
        {/* Top bar with date filter */}
        <div style={styles.topBar}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>Filter by Date:</span>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="YYYY-MM-DD HH:mm"
            showTime={{ format: 'HH:mm' }}
            placeholder={['Start Date & Time', 'End Date & Time']}
            size="small"
            getPopupContainer={trigger => trigger.parentElement}
          />
          {dateRange && (
            <Button variant="outline-secondary" size="sm" onClick={() => setDateRange(null)}>
              Clear
            </Button>
          )}
          <span style={{ fontSize: 12, color: '#888', marginLeft: 'auto' }}>
            {filteredComments.length} comment{filteredComments.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Two-column layout */}
        <div style={styles.contentWrapper}>
          {/* Left Column - Lead Details */}
          <div style={styles.leadDetailsColumn}>
            {selectedLead && (
              <>
                <div style={styles.leadBadge}>
                  {selectedLead.name}&nbsp;·&nbsp;ID: {selectedLead.leadid}
                </div>
                
                <div style={styles.fieldsGrid}>
                  {displayFields.map(key => {
                    const fieldDef = allFields.find(f => f.key === key);
                    const label = fieldDef?.label || key;
const value = formatValue(key, selectedLead[key], selectedLead);                    
                    return (
                      <div key={key} style={styles.fieldCell}>
                        <div style={styles.fieldLabel}>{label}</div>
                        <div style={{
                          ...styles.fieldValue,
                          color: value === '-' ? '#bbb' : '#222',
                          fontWeight: value === '-' ? 400 : 500,
                        }}>
                          {value}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Right Column - Comments */}
          <div style={styles.commentsColumn}>
            {isLoading ? (
              <p style={styles.emptyState}>Loading comments...</p>
            ) : filteredComments.length === 0 ? (
              <p style={styles.emptyState}>
                {dateRange 
                  ? 'No comments in the selected date range' 
                  : 'No comments found for this lead'}
              </p>
            ) : (
              filteredComments.map((comment, index) => (
                <div key={comment.id || index} style={styles.commentCard}>
                  <div style={styles.commentTime}>
                    {new Date(comment.timestamp).toLocaleString('en-IN', {
                      timeZone: 'Asia/Kolkata',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true,
                    })}
                  </div>
                  <div style={styles.commentText}>
                    <span style={styles.commentAuthor}>{comment.name}</span>
                    <span style={{ color: '#555', margin: '0 4px' }}>:</span>
                    <span>{comment.text}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer style={{ padding: '8px 16px' }}>
        <Button variant="secondary" size="sm" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReportComments;